import { JsonViewer } from '@/components/JsonViewer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { normalizeBrandDomain } from '@rock/brands';
import { normalizeBrandName } from '@rock/util';
import type { Brand, BrandAssetSlot, BrandfetchCandidate, BrandLogoImage, BrandSource } from '@rock/types';
import { BadgeCheck, Pencil, Pin } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { BrandAssetsCard } from './BrandAssetsCard';
import { isDomainAlignedWithBrandfetch } from './brandfetchAlignment';

export interface BrandDetailsUpdate {
    canonicalName: string;
    displayName: string;
    website: string;
    description: string;
}

interface BrandDetailsTabProps {
    brand: Brand;
    source: BrandSource | undefined;
    isEnriching: boolean;
    brandfetchCandidates: BrandfetchCandidate[];
    onBrandfetch: (domain?: string) => Promise<void>;
    onDismissCandidates: () => void;
    onPinDomain: (domain: string) => Promise<void>;
    isSearchingLogos: boolean;
    logoOptions: BrandLogoImage[];
    onSearchLogos: (query: string) => Promise<void>;
    onAssignAsset: (slot: BrandAssetSlot, asset: BrandLogoImage) => Promise<void>;
    onClearAsset: (slot: BrandAssetSlot) => Promise<void>;
    onSave: (updates: BrandDetailsUpdate) => Promise<void>;
    onApplyDisplayName: (displayName: string) => Promise<void>;
}

const DetailItem: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
    <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        <span className="break-all text-base text-foreground">{value ?? '—'}</span>
    </div>
);

export const BrandDetailsTab: FC<BrandDetailsTabProps> = ({
    brand,
    source,
    isEnriching,
    brandfetchCandidates,
    onBrandfetch,
    onDismissCandidates,
    onPinDomain,
    isSearchingLogos,
    logoOptions,
    onSearchLogos,
    onAssignAsset,
    onClearAsset,
    onSave,
    onApplyDisplayName,
}) => {
    const [editOpen, setEditOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    // Editing the canonical name is opt-in — see `canonicalNameHint` for what the override is warning about.
    const [nameOverride, setNameOverride] = useState(false);
    const [displayNameInput, setDisplayNameInput] = useState('');
    const [websiteInput, setWebsiteInput] = useState('');
    const [descriptionInput, setDescriptionInput] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isPinning, setIsPinning] = useState(false);
    const [isApplyingName, setIsApplyingName] = useState(false);

    // The website is an implicit owned domain that the next Brandfetch enrichment rewrites — offer to
    // pin it into the explicit list unless it already survives a refetch through either durable list
    // (see `withBrandDomainPinned`).
    const websiteHost = brand.website ? normalizeBrandDomain(brand.website) : null;
    const canPinWebsite =
        !!websiteHost &&
        !(brand.domains ?? []).includes(websiteHost) &&
        !(brand.researchedDomains ?? []).includes(websiteHost);

    const handlePinWebsite = async () => {
        if (!websiteHost || isPinning) return;
        setIsPinning(true);
        try {
            await onPinDomain(websiteHost);
        } finally {
            setIsPinning(false);
        }
    };

    // Folded once here: `display` is what the DAO will store, `key` is the identity it would resolve as.
    const folded = normalizeBrandName(nameInput);
    const nameError = canonicalNameError(brand, nameInput, folded);
    const nameNote = nameError ?? canonicalNameHint(brand, nameOverride);

    const brandfetchName = source?.payload.name?.trim();

    const handleApplyBrandfetchName = async (name: string) => {
        if (isApplyingName) return;
        setIsApplyingName(true);
        try {
            await onApplyDisplayName(name);
        } catch {
            // Toast handled by the page.
        } finally {
            setIsApplyingName(false);
        }
    };

    const openEdit = () => {
        setNameInput(brand.canonicalName);
        setNameOverride(false);
        setDisplayNameInput(brand.displayName ?? '');
        setWebsiteInput(brand.website ?? '');
        setDescriptionInput(brand.description ?? '');
        setEditOpen(true);
    };

    const handleSave = async () => {
        if (!nameInput.trim() || nameError || isSaving) return;
        setIsSaving(true);
        try {
            await onSave({
                canonicalName: nameInput.trim(),
                displayName: displayNameInput.trim(),
                website: websiteInput.trim(),
                description: descriptionInput.trim(),
            });
            setEditOpen(false);
        } catch {
            // Toast handled by the page; keep the dialog open so nothing is lost.
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle>Summary</CardTitle>
                            <CardDescription>Key details from the brand document.</CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={openEdit}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <DetailItem
                            label="Display name"
                            value={
                                <span className="flex flex-col items-start gap-1">
                                    {brand.displayName ?? '—'}
                                    <BrandfetchNameChip
                                        name={brandfetchName}
                                        current={brand.displayName}
                                        disabled={isApplyingName}
                                        onApply={(name) => void handleApplyBrandfetchName(name)}
                                    />
                                </span>
                            }
                        />
                        <DetailItem label="Canonical name" value={brand.canonicalName} />
                        <DetailItem
                            label="Canonical key"
                            value={<span className="font-mono">{brand.canonicalKey}</span>}
                        />
                        <DetailItem
                            label="Website"
                            value={
                                brand.website ? (
                                    <span className="flex flex-col items-start gap-1">
                                        <span className="flex items-center gap-1.5">
                                            <a
                                                href={brand.website}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-primary underline-offset-2 hover:underline"
                                            >
                                                {brand.website}
                                            </a>
                                            {isDomainAlignedWithBrandfetch(brand) ? (
                                                <span title="Matches Brandfetch">
                                                    <BadgeCheck
                                                        className="h-4 w-4 shrink-0 text-emerald-600"
                                                        aria-label="Matches Brandfetch"
                                                    />
                                                </span>
                                            ) : null}
                                        </span>
                                        {canPinWebsite ? (
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="h-auto p-0 text-xs"
                                                onClick={() => void handlePinWebsite()}
                                                disabled={isPinning}
                                                title="Copy this domain into the brand’s owned list so a refetch can’t drop it"
                                            >
                                                <Pin className="mr-1 h-3 w-3" />
                                                Add to owned domains
                                            </Button>
                                        ) : null}
                                    </span>
                                ) : undefined
                            }
                        />
                        <DetailItem
                            label="Primary color"
                            value={
                                brand.primaryColor ? (
                                    <span className="flex items-center gap-2">
                                        <span
                                            className="inline-block h-4 w-4 rounded border"
                                            style={{ backgroundColor: brand.primaryColor }}
                                        />
                                        <span className="font-mono">{brand.primaryColor}</span>
                                    </span>
                                ) : undefined
                            }
                        />
                        <div className="sm:col-span-2">
                            <DetailItem label="Description" value={brand.description} />
                        </div>
                        <DetailItem label="Created" value={new Date(brand.createdAt).toLocaleString()} />
                        <DetailItem label="Updated" value={new Date(brand.updatedAt).toLocaleString()} />
                    </div>
                </CardContent>
            </Card>

            <BrandAssetsCard
                brand={brand}
                source={source}
                isEnriching={isEnriching}
                brandfetchCandidates={brandfetchCandidates}
                onBrandfetch={onBrandfetch}
                onDismissCandidates={onDismissCandidates}
                isSearchingLogos={isSearchingLogos}
                logoOptions={logoOptions}
                onSearchLogos={onSearchLogos}
                onAssignAsset={onAssignAsset}
                onClearAsset={onClearAsset}
            />

            <Card>
                <CardHeader>
                    <CardTitle>Object</CardTitle>
                    <CardDescription>The complete brand payload stored in Firestore.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                        <JsonViewer data={brand} />
                    </div>
                </CardContent>
            </Card>

            <Dialog open={editOpen} onOpenChange={(isOpen) => !isSaving && setEditOpen(isOpen)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit brand</DialogTitle>
                        <DialogDescription>
                            Display fields only — the canonical key{' '}
                            <span className="font-mono">{brand.canonicalKey}</span> and alias lookups are unaffected.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col gap-3 py-2">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between gap-2">
                                <label htmlFor="brand-edit-name" className="text-sm font-medium text-muted-foreground">
                                    Canonical name
                                </label>
                                {!nameOverride && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        aria-label="Override canonical name"
                                        onClick={() => setNameOverride(true)}
                                        disabled={isSaving}
                                    >
                                        Override
                                    </Button>
                                )}
                            </div>
                            <Input
                                id="brand-edit-name"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                // `readOnly`, not `disabled`, while locked: the value is the thing the admin is being
                                // asked to read before overriding, so it stays focusable and at full contrast.
                                readOnly={!nameOverride}
                                disabled={isSaving}
                                aria-invalid={!!nameError}
                                aria-describedby="brand-edit-name-note"
                                className={cn(!nameOverride && 'bg-muted')}
                            />
                            {/* Always mounted, so a screen reader announces a note that appears as the admin types. */}
                            <span
                                id="brand-edit-name-note"
                                role="status"
                                aria-live="polite"
                                className={cn('text-sm', nameError ? 'text-destructive' : 'text-muted-foreground')}
                            >
                                {nameNote}
                            </span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label
                                htmlFor="brand-edit-display-name"
                                className="flex flex-col gap-1 text-sm font-medium text-muted-foreground"
                            >
                                Display name
                                <Input
                                    id="brand-edit-display-name"
                                    value={displayNameInput}
                                    onChange={(e) => setDisplayNameInput(e.target.value)}
                                    placeholder="Properly-cased name, e.g. “Whirlpool”"
                                    disabled={isSaving}
                                />
                            </label>
                            <BrandfetchNameChip
                                name={brandfetchName}
                                current={displayNameInput}
                                disabled={isSaving}
                                onApply={setDisplayNameInput}
                            />
                        </div>
                        <label
                            htmlFor="brand-edit-website"
                            className="flex flex-col gap-1 text-sm font-medium text-muted-foreground"
                        >
                            Website
                            <Input
                                id="brand-edit-website"
                                value={websiteInput}
                                onChange={(e) => setWebsiteInput(e.target.value)}
                                placeholder="https://…"
                                disabled={isSaving}
                            />
                        </label>
                        <label
                            htmlFor="brand-edit-description"
                            className="flex flex-col gap-1 text-sm font-medium text-muted-foreground"
                        >
                            Description
                            <Input
                                id="brand-edit-description"
                                value={descriptionInput}
                                onChange={(e) => setDescriptionInput(e.target.value)}
                                disabled={isSaving}
                            />
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={!nameInput.trim() || !!nameError || isSaving}>
                            {isSaving ? 'Saving…' : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

/** Brandfetch's name for the company, offered as a one-click display name while it differs from `current`. */
const BrandfetchNameChip: FC<{
    name: string | undefined;
    current: string | undefined;
    disabled: boolean;
    onApply: (name: string) => void;
}> = ({ name, current, disabled, onApply }) => {
    if (!name || name === current?.trim()) return null;
    return (
        <button
            type="button"
            className="self-start disabled:pointer-events-none disabled:opacity-50"
            onClick={() => onApply(name)}
            disabled={disabled}
            title="Brandfetch’s name for the company. Use it only if it matches the nameplate."
        >
            <Badge variant="outline" className="cursor-pointer hover:bg-muted">
                Brandfetch: {name}
            </Badge>
        </button>
    );
};

/**
 * Why a typed canonical name can't be saved, or undefined when it can. Advisory only — the DAO decides, and
 * it checks the `brand-aliases` doc where this reads the brand's display list, so a legacy alias with no
 * projection doc is accepted here and refused there.
 *
 * Only a CHANGED name is judged, matching the guard: a brand whose stored name already resolves nowhere
 * has to stay editable in its other fields.
 */
function canonicalNameError(brand: Brand, typed: string, folded: { display: string; key: string }): string | undefined {
    if (typed.trim() && !folded.key) {
        return `“${typed.trim()}” folds to nothing — a name needs letters or digits to key on.`;
    }
    if (!folded.display || folded.display === brand.canonicalName) return undefined;
    if (brandAnswersTo(brand, folded.key)) return undefined;
    return (
        `“${folded.display}” keys to ${folded.key}, which is neither this brand's identity key ` +
        `${brand.canonicalKey} nor one of its aliases. Add it on the Aliases tab first, then it can become the ` +
        `canonical name.`
    );
}

/** What to say about a name that saves fine: what the override is for, or that this brand's name already drifts. */
function canonicalNameHint(brand: Brand, override: boolean): string | undefined {
    if (override) {
        return (
            `Corrections only — spacing, punctuation, casing. This name is the display half of ` +
            `${brand.canonicalKey}, the key every lookup resolves through, which never changes.`
        );
    }
    const storedKey = normalizeBrandName(brand.canonicalName).key;
    if (!brandAnswersTo(brand, storedKey)) {
        return (
            `This stored name keys to ${storedKey}, which is neither this brand's ${brand.canonicalKey} nor one of ` +
            `its aliases — a rename that predates the guard. Other fields still save.`
        );
    }
    return undefined;
}

/**
 * Does `key` name an identity this brand answers to — its own key, or one of its aliases? A canonical name
 * folding to an alias key is legitimate curation: prod's GE keys on GE and is named "GENERAL ELECTRIC",
 * which is registered as its alias, so both spellings resolve to it.
 */
function brandAnswersTo(brand: Brand, key: string): boolean {
    return key === brand.canonicalKey || brand.aliases.some((alias) => normalizeBrandName(alias).key === key);
}
